import { MODAL_ELEMENT_ID_PREFIX } from "Common/componentUtils/constants";
import useIsMountedRef from "Common/componentUtils/useIsMountedRef";
import { isDevMode } from "Common/debug/debug";
import withOverlaysContext from "Components/overlaysContext/withOverlaysContext";
import get from "lodash/get";
import noop from "lodash/noop";
import startsWith from "lodash/startsWith";
import throttle from "lodash/throttle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactResizeDetector from "react-resize-detector";
const getWindowViewportHeight = () => get(document, "documentElement.clientHeight") ||
    get(window, "innerHeight") ||
    get(document, "body.clientHeight");
const THROTTLE_RATE = {
    SHORT: 5,
    LONG: 50
};
function HeightDetector(props) {
    const { minBottomOffset, wrapperRef, onChangeAvailableHeight, useFixedResizableElement } = props;
    const isMountedRef = useIsMountedRef();
    const rafId = useRef(0);
    const [bottom, setBottom] = useState(-1);
    const [top, setTop] = useState(-1);
    const resizableElementRef = useRef(null);
    const handleHeightChanged = (_, h) => {
        if (h && isMountedRef.current) {
            onChangeAvailableHeight(h);
        }
    };
    const updateTopBottom = () => {
        var _a;
        if (!((_a = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect))
            return false;
        const clientRect = wrapperRef.current.getBoundingClientRect();
        const newBottom = getWindowViewportHeight() - clientRect.top - clientRect.height;
        setBottom(prev => {
            const bottomToSet = newBottom > minBottomOffset ? newBottom : minBottomOffset;
            if (prev === -1)
                return bottomToSet;
            const container = props.getOverlaysContainer && props.getOverlaysContainer();
            if (container && startsWith(get(container, "id"), MODAL_ELEMENT_ID_PREFIX))
                return bottomToSet;
            return prev;
        });
        setTop(clientRect.top);
        return true;
    };
    const handleResize = () => {
        if (!useFixedResizableElement)
            return;
        rafId.current && cancelAnimationFrame(rafId.current);
        rafId.current = requestAnimationFrame(() => {
            updateTopBottom();
        });
    };
    useEffect(() => {
        if (!useFixedResizableElement)
            return noop;
        if (!updateTopBottom()) {
            setBottom(0);
            setTop(0);
        }
        const resizeCallback = throttle(handleResize, 150);
        window.addEventListener("resize", resizeCallback);
        return () => {
            window.removeEventListener("resize", resizeCallback);
            rafId.current && cancelAnimationFrame(rafId.current);
        };
    }, []);
    const hasResizableElement = useFixedResizableElement && top >= 0 && bottom >= 0;
    const resizableElementStyle = useMemo(() => {
        if (!hasResizableElement)
            return undefined;
        const result = {
            display: "block",
            height: `calc(100vh - ${bottom + top}px)`,
            position: "fixed",
            top
        };
        if (isDevMode && window.showResizeDetectorTarget) {
            result.backgroundColor = "tomato";
            result.width = "5px";
            result.zIndex = Number.MAX_SAFE_INTEGER;
        }
        else {
            result.visibility = "hidden";
        }
        return result;
    }, [hasResizableElement, bottom, top]);
    return (React.createElement(React.Fragment, null,
        hasResizableElement && React.createElement("div", { ref: resizableElementRef, style: resizableElementStyle }),
        React.createElement(ReactResizeDetector, { render: () => undefined, targetRef: hasResizableElement ? resizableElementRef : wrapperRef, handleHeight: true, onResize: handleHeightChanged, refreshMode: "throttle", refreshRate: !hasResizableElement ? THROTTLE_RATE.SHORT : THROTTLE_RATE.LONG })));
}
export default withOverlaysContext(HeightDetector);
HeightDetector.defaultProps = {
    minBottomOffset: 40,
    useFixedResizableElement: true
};
